import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import './Academics.css';
import { useAuth } from '../context/AuthContext';
import { getDatabase, ref, onValue, off, remove } from 'firebase/database';
import MainLayout from './MainLayout';

const Academics = () => {
    const { currentUser } = useAuth();
    const [userData, setUserData] = useState(null);
    const [allClasses, setAllClasses] = useState([]);
    const [assignedSubjects, setAssignedSubjects] = useState([]);
    const [selectedClass, setSelectedClass] = useState('');
    const [selectedSession, setSelectedSession] = useState('');
    const [selectedTerm, setSelectedTerm] = useState('');
    const [selectedSubject, setSelectedSubject] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const db = getDatabase();

    const sessions = useMemo(() => Array.from({ length: 5 }, (_, i) => {
        const year = new Date().getFullYear() - i;
        return `${year}/${year + 1}`;
    }), []);
    const terms = useMemo(() => ['FIRST TERM', 'SECOND TERM', 'THIRD TERM'], []);

    useEffect(() => {
        if (!currentUser) {
            setLoading(false);
            return;
        }

        const userRef = ref(db, `users/${currentUser.uid}`);
        const userListener = onValue(userRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                setUserData(data);
                const role = data.role?.toLowerCase();
                if (role === 'form-master') {
                    setSelectedClass(data.assignedClass || '');
                }
            } else {
                setError("Could not find your user data.");
            }
            setLoading(false);
        }, (err) => {
            console.error("Error fetching user data:", err);
            setError("Failed to fetch your details.");
            setLoading(false);
        });

        return () => off(userRef, 'value', userListener);
    }, [currentUser, db]);

    useEffect(() => {
        const appSettingsRef = ref(db, 'appSettings/currentSession');
        const listener = onValue(appSettingsRef, (snapshot) => {
            if (snapshot.exists() && snapshot.val()) {
                const { session, term } = snapshot.val();
                setSelectedSession(session || sessions[0]);
                setSelectedTerm(term || terms[0]);
            } else {
                setSelectedSession(sessions[0]);
                setSelectedTerm(terms[0]);
            }
        });
        return () => off(appSettingsRef, 'value', listener);
    }, [db, sessions, terms]);

    useEffect(() => {
        const role = userData?.role?.toLowerCase();
        if (['admin', 'principal'].includes(role)) {
            const classesRef = ref(db, 'classes');
            const classesListener = onValue(classesRef, (snapshot) => {
                setAllClasses(snapshot.exists() ? Object.keys(snapshot.val()).sort() : []);
            }, (err) => {
                console.error("Error fetching classes:", err);
                setError("Failed to load classes.");
            });
            return () => off(classesRef, 'value', classesListener);
        }
    }, [userData, db]);

    useEffect(() => {
        const role = userData?.role?.toLowerCase();
        if (!selectedClass || !role) return;

        if (['admin', 'principal'].includes(role)) {
            const subjectsRef = ref(db, `classSubjects/${selectedClass}`);
            const callback = (snapshot) => {
                const val = snapshot.val();
                setAssignedSubjects(Array.isArray(val) ? val : Object.keys(val || {}));
            };
            onValue(subjectsRef, callback);
            return () => off(subjectsRef, 'value', callback);
        }

        if (['teacher', 'form-master'].includes(role) && userData.assignedSubjects) {
            let subjectsSource = userData.assignedSubjects;
            if (subjectsSource[selectedClass]) {
                subjectsSource = subjectsSource[selectedClass];
            }

            let subjects = [];
            if (subjectsSource) {
                if (Array.isArray(subjectsSource)) {
                    subjects = subjectsSource.filter(Boolean);
                } else if (typeof subjectsSource === 'object') {
                    subjects = Object.values(subjectsSource);
                }
            }
            setAssignedSubjects(subjects.sort());
        }
    }, [selectedClass, userData, db]);

    const handleDeleteCAData = async () => {
        if (!selectedSession || !selectedTerm || !selectedClass || !selectedSubject) {
            alert("Please select session, term, class, and subject to delete data.");
            return;
        }

        if (window.confirm(`Are you sure you want to delete all CA data for ${selectedSubject} in ${selectedClass} for ${selectedTerm}, ${selectedSession}? This action cannot be undone.`)) {
            const sessionPath = encodeURIComponent(selectedSession);
            const termPath = encodeURIComponent(selectedTerm.toUpperCase());
            const path = `ca_scores/${sessionPath}/${termPath}/${selectedClass}/${selectedSubject}`;
            try {
                await remove(ref(db, path));
                alert("CA data deleted successfully.");
            } catch (error) {
                console.error("Error deleting CA data:", error);
                alert("Failed to delete CA data.");
            }
        }
    };

    const role = userData?.role?.toLowerCase();
    const canSelectClass = userData && ['admin', 'principal'].includes(userData.role?.toLowerCase());
    const allowed = ['admin', 'principal', 'form-master'].includes(role);

    if (loading) {
        return <MainLayout><div>Loading...</div></MainLayout>;
    }

    if (error) {
        return <MainLayout><div className="alert alert-danger">{error}</div></MainLayout>;
    }

    if (!allowed) {
        return (
            <MainLayout>
                <h1>Access Denied</h1>
                <p>You do not have permission to access this page.</p>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="academics-container">
                <div className="entry-form">
                    <form>
                        <div className="filters-container row mb-3">
                            <div className="col-md-3">
                                <label>Session</label>
                                <select className="form-control" value={selectedSession} onChange={(e) => setSelectedSession(e.target.value)}>
                                    <option value="">Select Session</option>
                                    {sessions.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                            <div className="col-md-3">
                                <label>Term</label>
                                <select className="form-control" value={selectedTerm} onChange={(e) => setSelectedTerm(e.target.value)}>
                                    <option value="">Select Term</option>
                                    {terms.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div className="col-md-3">
                                <label>Class</label>
                                <select
                                  className="form-control"
                                  value={selectedClass}
                                  onChange={(e) => setSelectedClass(e.target.value)}
                                  disabled={!canSelectClass}
                                >
                                  {canSelectClass ? (
                                    <>
                                      <option value="">-- Select Class --</option>
                                      {allClasses.map((c) => (
                                        <option key={c} value={c}>
                                          {c}
                                        </option>
                                      ))}
                                    </>
                                  ) : (
                                    <option value={selectedClass}>{selectedClass}</option>
                                  )}
                                </select>
                            </div>
                            <div className="col-md-3">
                                <label>Subject</label>
                                <select
                                  className="form-control"
                                  value={selectedSubject}
                                  onChange={(e) => setSelectedSubject(e.target.value)}
                                >
                                  <option value="">-- Select Subject --</option>
                                  {Array.isArray(assignedSubjects) &&
                                    assignedSubjects.map((s) => (
                                      <option key={s} value={s}>
                                        {s}
                                      </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="form-group buttons">
                            {/* <button type="button" className="btn btn-success" onClick={handleGenerateTemplate}>Generate assessment template</button> */}
                            {/* <button type="button" className="btn btn-secondary" onClick={handleDownloadTemplate}>Download assessment template</button> */}
                            <Link to="/update-ca" className="btn btn-danger">Assessment update</Link>
                            <Link to="/traits-assessment" className="btn btn-info">Traits update</Link>
                            <Link to="/report-admin" className="btn btn-warning">Report Sheet Admin</Link>
                            <button type="button" className="btn btn-primary" onClick={handleDeleteCAData}>Delete CA Data</button>
                        </div>
                    </form>
                </div>
            </div>
        </MainLayout>
    );
};

export default Academics;
